import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Search, UserPlus, UserMinus, ChevronRight, Activity, 
  Ruler, X, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import AthleteInfoDisplay from './shared/AthleteInfoDisplay';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function AllAthletesManager({ onAthleteClick, onAssignSuccess }) {
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [currentCoach, setCurrentCoach] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [coachFilter, setCoachFilter] = useState('all');
  const [assigning, setAssigning] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadAthletes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (coachFilter && coachFilter !== 'all') params.append('coach_filter', coachFilter);
      
      const response = await axios.get(`${API}/children/all-athletes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAthletes(response.data.athletes);
      setCoaches(response.data.coaches || []);
      setCurrentCoach(response.data.current_coach || '');
    } catch (error) {
      console.error('Failed to load athletes:', error);
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, coachFilter]);

  useEffect(() => {
    loadAthletes();
  }, [loadAthletes]);

  const assignToMe = async (athleteId, athleteName) => {
    setAssigning(athleteId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${athleteId}/assign-coach`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${athleteName} assigned to you`);
      loadAthletes();
      if (onAssignSuccess) onAssignSuccess();
    } catch (error) {
      toast.error('Failed to assign athlete');
    } finally {
      setAssigning(null);
    }
  };

  const unassignFromMe = async (athleteId, athleteName) => {
    setAssigning(athleteId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/children/${athleteId}/unassign-coach`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${athleteName} unassigned`);
      loadAthletes();
      if (onAssignSuccess) onAssignSuccess();
    } catch (error) {
      toast.error('Failed to unassign athlete');
    } finally {
      setAssigning(null);
    }
  };

  const getCoachBadgeColor = (coachName) => {
    if (!coachName) return 'bg-zinc-700 text-zinc-400';
    if (coachName === currentCoach) return 'bg-[#02b5ff]/20 text-[#02b5ff]';
    return 'bg-[#ff025f]/20 text-[#ff025f]';
  };

  const filteredAthletes = athletes;

  // Group athletes by assignment status for summary
  const unassignedCount = athletes.filter(a => !a.assigned_coach_name).length;
  const myAthletesCount = athletes.filter(a => a.is_mine).length;
  const otherCoachCount = athletes.filter(a => a.assigned_coach_name && !a.is_mine).length;

  return (
    <div className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden" data-testid="all-athletes-manager">
      {/* Header */}
      <div className="p-6 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-xl flex items-center justify-center">
              <Users className="h-5 w-5 text-[#02b5ff]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">All Athletes</h2>
              <p className="text-xs text-zinc-500">
                {athletes.length} total • {unassignedCount} unassigned • {myAthletesCount} yours
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadAthletes}
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              placeholder="Search by name (e.g., Joseph Cridge)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              data-testid="athlete-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Select value={coachFilter} onValueChange={setCoachFilter}>
            <SelectTrigger className="w-full sm:w-[200px] h-10 bg-zinc-800 border-zinc-700 text-white">
              <Filter className="h-4 w-4 mr-2 text-zinc-500" />
              <SelectValue placeholder="Filter by coach" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-white hover:bg-zinc-700">All Athletes</SelectItem>
              <SelectItem value="unassigned" className="text-white hover:bg-zinc-700">
                {`Unassigned (${unassignedCount})`}
              </SelectItem>
              <SelectItem value="mine" className="text-white hover:bg-zinc-700">
                {`My Athletes (${myAthletesCount})`}
              </SelectItem>
              <SelectItem value="other" className="text-white hover:bg-zinc-700">
                {`Other Coaches (${otherCoachCount})`}
              </SelectItem>
              {coaches.length > 0 && (
                <>
                  <div className="h-px bg-zinc-700 my-1" />
                  {coaches.map(coach => (
                    <SelectItem key={coach} value={coach} className="text-white hover:bg-zinc-700">
                      {coach}
                    </SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Athletes List */}
      <div className="max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500">
            <div className="inline-flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-[#02b5ff] border-t-transparent rounded-full animate-spin" />
              Loading athletes...
            </div>
          </div>
        ) : filteredAthletes.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 font-medium">No athletes found</p>
            <p className="text-zinc-600 text-sm mt-1">
              {searchTerm ? `No results for "${searchTerm}"` : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {filteredAthletes.map((athlete) => (
              <div
                key={athlete.id}
                className="p-4 hover:bg-zinc-800/50 transition-colors"
                data-testid={`athlete-row-${athlete.id}`}
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Athlete Info */}
                  <div 
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => onAthleteClick && onAthleteClick(athlete.id)}
                  >
                    <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-zinc-400">
                        {athlete.first_name?.[0]}{athlete.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">
                        {athlete.first_name} {athlete.last_name}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        {athlete.primary_sport && (
                          <span>{athlete.primary_sport}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          {athlete.test_count} tests
                        </span>
                        <span className="flex items-center gap-1">
                          <Ruler className="h-3 w-3" />
                          {athlete.measurement_count} measurements
                        </span>
                      </div>
                      {/* Team & Manager Info - Using shared component */}
                      <AthleteInfoDisplay 
                        teamName={athlete.current_team_name}
                        managerName={athlete.assigned_team_manager_name}
                        compact={true}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Coach Badge */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCoachBadgeColor(athlete.assigned_coach_name)}`}>
                      {athlete.assigned_coach_name || 'Unassigned'}
                    </span>

                    {/* Action Button */}
                    {athlete.is_mine ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unassignFromMe(athlete.id, `${athlete.first_name} ${athlete.last_name}`)}
                        disabled={assigning === athlete.id}
                        className="h-8 px-3 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`unassign-btn-${athlete.id}`}
                      >
                        {assigning === athlete.id ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <UserMinus className="h-4 w-4 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => assignToMe(athlete.id, `${athlete.first_name} ${athlete.last_name}`)}
                        disabled={assigning === athlete.id}
                        className="h-8 px-3 text-[#02b5ff] hover:text-white hover:bg-[#02b5ff]/20"
                        data-testid={`assign-btn-${athlete.id}`}
                      >
                        {assigning === athlete.id ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-1" />
                            Assign to Me
                          </>
                        )}
                      </Button>
                    )}

                    {/* View Profile Arrow */}
                    <button
                      onClick={() => onAthleteClick && onAthleteClick(athlete.id)}
                      className="p-2 text-zinc-500 hover:text-white transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
